import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LabelList
} from 'recharts';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import DemoBlockModal from '../components/DemoBlockModal';
import { isDemoUser } from '../utils/demoGuard';
import './ERDashboard.css';
import { BIGQUERY_CONFIG } from '../config';

import { API_BASE_URL } from '../config';

const PROJECT_ID = BIGQUERY_CONFIG.PROJECT;
const DATASET_NAME = BIGQUERY_CONFIG.DATASET;
const TABLE_NAME = 'PHY_PY';

// Formatter functions
const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const formatRiskScore = (value) => (Number(value || 0)).toFixed(3);
const formatNumber = (value) => Number(value || 0).toLocaleString('en-US');

function PHYDashboardMain({ activeView, setActiveView }) {
  const { user, getAuthHeader } = useAuth();
  const [showDemoBlock, setShowDemoBlock] = React.useState(false);
  const [metrics, setMetrics] = useState({
    total_patients: 0,
    total_visits: 0,
    total_cost: 0,
    avg_risk_score: 0
  });
  const [data, setData] = useState({
    monthlyVisits: [],
    monthlyCost: [],
    topHcpcs: [],
    topDiagnosis: [],
    topCCSR: [],
    topChronicConditions: [],
    byCohort: [],
    byRiskGroup: []
  });
  const [viewMode, setViewMode] = useState('visits'); // 'visits' or 'cost'
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [filters, setFilters] = useState({
    'BENE_MBI_ID': ['All'],
    'CLM_LINE_HCPCS_CD': ['All'],
    'PRNCPL_DGNS_CD': ['All'],
    'CLM_RNDRG_PRVDR_TAX_NUM': ['All'],
    'CLM_RNDRG_PRVDR_NPI_NUM': ['All'],
    'yr_mo': ['All'],
    'npi_first_name': ['All'],
    'npi_last_name': ['All'],
    'npi_business_legal_name': ['All'],
    'ccsr': ['All'],
    'CHRONIC': ['All'],
    'BENE_1ST_NAME': ['All'],
    'BENE_LAST_NAME': ['All'],
    'Gender': ['All'],
    'COHORT': ['All']
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Re-sort data when viewMode changes
  useEffect(() => {
    if (data.topHcpcs.length > 0) {
      const sortDescending = (dataArray) => {
        return [...dataArray].sort((a, b) => {
          const aVal = viewMode === 'cost' ? a.cost_value : a.value;
          const bVal = viewMode === 'cost' ? b.cost_value : b.value;
          return bVal - aVal;
        });
      };
      
      setData(prevData => ({
        ...prevData,
        topHcpcs: sortDescending(prevData.topHcpcs),
        topDiagnosis: sortDescending(prevData.topDiagnosis),
        topCCSR: sortDescending(prevData.topCCSR),
        topChronicConditions: sortDescending(prevData.topChronicConditions),
        byCohort: sortDescending(prevData.byCohort)
      }));
    }
  }, [viewMode]);

  const handleFilterChange = (field, values) => {
    console.log(`Filter changed - Field: ${field}, Values:`, values);
    setFilters(prev => ({ ...prev, [field]: values }));
  };

  const handleResetFilters = () => {
    setFilters({
      'BENE_MBI_ID': ['All'],
      'CLM_LINE_HCPCS_CD': ['All'],
      'PRNCPL_DGNS_CD': ['All'],
      'CLM_RNDRG_PRVDR_TAX_NUM': ['All'],
      'CLM_RNDRG_PRVDR_NPI_NUM': ['All'],
      'yr_mo': ['All'],
      'npi_first_name': ['All'],
      'npi_last_name': ['All'],
      'npi_business_legal_name': ['All'],
      'ccsr': ['All'],
      'CHRONIC': ['All'],
      'BENE_1ST_NAME': ['All'],
      'BENE_LAST_NAME': ['All'],
      'Gender': ['All'],
      'COHORT': ['All']
    });
  };

  const replaceNullNames = (dataArray) => {
    return dataArray.map(item => ({
      ...item,
      name: item.name === null || item.name === 'null' ? 'Unknown' : item.name
    }));
  };

  const addPercentages = (dataArray, totalValue, mode) => {
    return dataArray.map(item => {
      const baseValue = mode === 'cost' ? item.cost_value : item.value;
      return {
        ...item,
        percentage: totalValue > 0 ? ((baseValue / totalValue) * 100).toFixed(1) : '0.0'
      };
    });
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        project: PROJECT_ID,
        dataset: DATASET_NAME,
        table: TABLE_NAME,
        filters: JSON.stringify(filters)
      });

      const response = await axios.get(`${API_BASE_URL}/api/phy/combined?${queryParams}`, {});

      const apiData = response.data;
      
      setMetrics({
        total_patients: apiData.total_patients || 0,
        total_visits: apiData.total_visits || 0,
        total_cost: apiData.total_cost || 0,
        avg_risk_score: apiData.avg_risk_score || 0
      });

      const totalVisits = apiData.total_visits || 0;
      const totalCost = apiData.total_cost || 0;

      setData({
        monthlyVisits: replaceNullNames(apiData.monthly_visits || []),
        monthlyCost: replaceNullNames(apiData.monthly_cost || []),
        topHcpcs: replaceNullNames(addPercentages(apiData.by_hcpcs || [], totalVisits, 'visits')).map(item => ({
          ...item,
          cost_percentage: totalCost > 0 ? ((item.cost_value / totalCost) * 100).toFixed(1) : '0.0'
        })),
        topDiagnosis: replaceNullNames(addPercentages(apiData.by_diagnosis || [], totalVisits, 'visits')).map(item => ({
          ...item,
          cost_percentage: totalCost > 0 ? ((item.cost_value / totalCost) * 100).toFixed(1) : '0.0'
        })),
        topCCSR: replaceNullNames(addPercentages(apiData.by_ccsr || [], totalVisits, 'visits')).map(item => ({
          ...item,
          cost_percentage: totalCost > 0 ? ((item.cost_value / totalCost) * 100).toFixed(1) : '0.0'
        })),
        topChronicConditions: replaceNullNames(addPercentages(apiData.by_chronic_condition || [], totalVisits, 'visits')).map(item => ({
          ...item,
          cost_percentage: totalCost > 0 ? ((item.cost_value / totalCost) * 100).toFixed(1) : '0.0'
        })),
        byCohort: replaceNullNames(addPercentages(apiData.by_cohort || [], totalVisits, 'visits')).map(item => ({
          ...item,
          cost_percentage: totalCost > 0 ? ((item.cost_value / totalCost) * 100).toFixed(1) : '0.0'
        })),
        byRiskGroup: replaceNullNames(apiData.by_risk_group || []).map(item => ({
          ...item,
          percentage: totalVisits > 0 ? ((item.value / totalVisits) * 100).toFixed(1) : '0.0',
          cost_percentage: totalCost > 0 ? ((item.cost_value / totalCost) * 100).toFixed(1) : '0.0'
        }))
      });

      setBeneficiaries(apiData.beneficiaries || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (isDemoUser(user)) { setShowDemoBlock(true); return; }
    if (beneficiaries.length === 0) {
      return;
    }

    const displayColumns = Object.keys(beneficiaries[0] || {});
    const headers = displayColumns;
    const csvContent = [
      headers.join(','),
      ...beneficiaries.map(row =>
        headers.map(header => {
          const value = row[header];
          return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `phy_py_deep_dive_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      return (
        <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '10px', borderRadius: '4px', fontSize: '13px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
          <p style={{margin: 0, fontWeight: 'bold'}}>{data.name}</p>
          {viewMode === 'visits' ? (
            <>
              <p style={{margin: '4px 0 0 0'}}>Visits: {formatNumber(data.value)}</p>
              <p style={{margin: '4px 0 0 0'}}>Percentage: {data.percentage}%</p>
            </>
          ) : (
            <>
              <p style={{margin: '4px 0 0 0'}}>Cost: {formatCurrency(data.cost_value)}</p>
              <p style={{margin: '4px 0 0 0'}}>Percentage: {data.cost_percentage}%</p>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="page-with-sidebar">
        <Sidebar filters={filters} onFilterChange={handleFilterChange} onResetFilters={handleResetFilters} />
        <div className="dashboard" style={{padding: '0px'}}>
          <div className="metrics-row" style={{padding: '20px 15px', marginBottom: '20px'}}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="metric-card" style={{
                animation: `pulse 1.5s ease-in-out infinite`,
                animationDelay: `${i * 0.1}s`,
                background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
                backgroundSize: '200% 100%'
              }}>
                <div style={{height: '14px', width: '80px', backgroundColor: '#d1d5db', borderRadius: '4px', marginBottom: '8px'}}></div>
                <div style={{height: '28px', width: '100px', backgroundColor: '#d1d5db', borderRadius: '4px'}}></div>
              </div>
            ))}
          </div>
          <div style={{padding: '0px 15px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
            {[1, 2].map((i) => (
              <div key={i} className="chart-card" style={{
                height: '300px',
                animation: `pulse 1.5s ease-in-out infinite`,
                animationDelay: `${(i + 4) * 0.1}s`,
                background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
                backgroundSize: '200% 100%'
              }}></div>
            ))}
          </div>
          <style>{`
            @keyframes pulse {
              0%, 100% { backgroundPosition: 200% 0; }
              50% { backgroundPosition: -200% 0; }
            }
          `}</style>
        </div>
      </div>
    );
  }

  const displayColumns = beneficiaries.length > 0 ? Object.keys(beneficiaries[0]) : [];

  return (
    <div className="page-with-sidebar">
      {showDemoBlock && <DemoBlockModal onClose={() => setShowDemoBlock(false)} />}
      <Sidebar filters={filters} onFilterChange={handleFilterChange} onResetFilters={handleResetFilters} />
      <div className="dashboard" style={{padding: '0px'}}>
        <div style={{animation: 'fadeIn 0.5s ease-in-out'}}>
          {/* Metrics Row */}
          <div className="metrics-row" style={{padding: '20px 15px', marginBottom: '20px'}}>
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>👥</span>
                <div>
                  <div className="metric-label">TOTAL PATIENTS</div>
                  <div className="metric-value">{formatNumber(metrics.total_patients)}</div>
                </div>
              </div>
            </div>
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>👨‍⚕️</span>
                <div>
                  <div className="metric-label">TOTAL VISITS</div>
                  <div className="metric-value">{formatNumber(metrics.total_visits)}</div>
                </div>
              </div>
            </div>
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>💰</span>
                <div>
                  <div className="metric-label">TOTAL COST</div>
                  <div className="metric-value">{formatCurrency(metrics.total_cost)}</div>
                </div>
              </div>
            </div>
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>📊</span>
                <div>
                  <div className="metric-label">AVG RISK SCORE</div>
                  <div className="metric-value">{formatRiskScore(metrics.avg_risk_score)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Line Charts */}
        <div style={{padding: '0px 15px', marginBottom: '30px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
          <div className="chart-card" style={{padding: '15px'}}>
            <h3 style={{margin: '0 0 15px 0'}}>Monthly Visits</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.monthlyVisits} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="yr_mo" style={{fontSize: '12px'}} />
                <YAxis style={{fontSize: '12px'}} />
                <Tooltip cursor={{ fill: '#e0f2fe' }} contentStyle={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '4px'}} />
                <Line type="monotone" dataKey="visits" stroke="#0284c7" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card" style={{padding: '15px'}}>
            <h3 style={{margin: '0 0 15px 0'}}>Monthly Cost</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.monthlyCost} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="yr_mo" style={{fontSize: '12px'}} />
                <YAxis style={{fontSize: '12px'}} tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
                <Tooltip cursor={{ fill: '#e0f2fe' }} contentStyle={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '4px'}} formatter={(value) => formatCurrency(value)} />
                <Line type="monotone" dataKey="cost" stroke="#0284c7" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Toggle for Visits/Cost */}
        <div style={{display: 'flex', justifyContent: 'center', marginBottom: '20px', gap: '10px'}}>
          <button
            onClick={() => setViewMode('visits')}
            style={{
              padding: '10px 30px',
              backgroundColor: viewMode === 'visits' ? '#0369a1' : '#e5e7eb',
              color: viewMode === 'visits' ? 'white' : '#1f2937',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
          >
            Visits
          </button>
          <button
            onClick={() => setViewMode('cost')}
            style={{
              padding: '10px 30px',
              backgroundColor: viewMode === 'cost' ? '#0369a1' : '#e5e7eb',
              color: viewMode === 'cost' ? 'white' : '#1f2937',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
          >
            Cost
          </button>
        </div>

        {/* Charts Grid */}
        <div className="charts-grid" style={{padding: '0px 15px', marginBottom: '30px'}}>
          {/* HCPCS Chart */}
          <div className="chart-card" style={{padding: '15px 0px'}}>
            <h3 style={{margin: '0 0 15px 20px'}}>
              {viewMode === 'visits' ? 'Visits' : 'Cost'} by Top 10 HCPCS
            </h3>
            <ResponsiveContainer width="100%" height={450}>
              <BarChart data={data.topHcpcs.slice(0, 10)} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={viewMode === 'cost' ? (v) => `$${(v/1000).toFixed(0)}K` : undefined} />
                <YAxis type="category" dataKey="name" width={150} style={{fontSize: '12px'}} interval={0} />
                <Tooltip cursor={{ fill: '#e0f2fe' }} content={CustomTooltip} />
                <Bar dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} fill="#0284c7">
                  <LabelList dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} position="insideRight" 
                    formatter={(value) => viewMode === 'cost' ? `$${(value/1000).toFixed(0)}K` : formatNumber(value)} 
                    style={{fontSize: '12px', fill: 'white'}} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Diagnosis Chart */}
          <div className="chart-card" style={{padding: '15px 0px'}}>
            <h3 style={{margin: '0 0 15px 20px'}}>
              {viewMode === 'visits' ? 'Visits' : 'Cost'} by Top 10 Diagnosis
            </h3>
            <ResponsiveContainer width="100%" height={450}>
              <BarChart data={data.topDiagnosis.slice(0, 10)} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={viewMode === 'cost' ? (v) => `$${(v/1000).toFixed(0)}K` : undefined} />
                <YAxis type="category" dataKey="name" width={150} style={{fontSize: '12px'}} interval={0} />
                <Tooltip cursor={{ fill: '#e0f2fe' }} content={CustomTooltip} />
                <Bar dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} fill="#0284c7">
                  <LabelList dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} position="insideRight" 
                    formatter={(value) => viewMode === 'cost' ? `$${(value/1000).toFixed(0)}K` : formatNumber(value)} 
                    style={{fontSize: '12px', fill: 'white'}} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* CCSR Chart */}
          <div className="chart-card" style={{padding: '15px 0px'}}>
            <h3 style={{margin: '0 0 15px 20px'}}>
              {viewMode === 'visits' ? 'Visits' : 'Cost'} by Top 10 CCSR
            </h3>
            <ResponsiveContainer width="100%" height={450}>
              <BarChart data={data.topCCSR.slice(0, 10)} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={viewMode === 'cost' ? (v) => `$${(v/1000).toFixed(0)}K` : undefined} />
                <YAxis type="category" dataKey="name" width={150} style={{fontSize: '12px'}} interval={0} />
                <Tooltip cursor={{ fill: '#e0f2fe' }} content={CustomTooltip} />
                <Bar dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} fill="#0284c7">
                  <LabelList dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} position="insideRight" 
                    formatter={(value) => viewMode === 'cost' ? `$${(value/1000).toFixed(0)}K` : formatNumber(value)} 
                    style={{fontSize: '12px', fill: 'white'}} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Chronic Conditions Chart */}
          <div className="chart-card" style={{padding: '15px 0px'}}>
            <h3 style={{margin: '0 0 15px 20px'}}>
              {viewMode === 'visits' ? 'Visits' : 'Cost'} by Top 10 Chronic Conditions
            </h3>
            <ResponsiveContainer width="100%" height={450}>
              <BarChart data={data.topChronicConditions.slice(0, 10)} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={viewMode === 'cost' ? (v) => `$${(v/1000).toFixed(0)}K` : undefined} />
                <YAxis type="category" dataKey="name" width={150} style={{fontSize: '12px'}} interval={0} />
                <Tooltip cursor={{ fill: '#e0f2fe' }} content={CustomTooltip} />
                <Bar dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} fill="#0284c7">
                  <LabelList dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} position="insideRight" 
                    formatter={(value) => viewMode === 'cost' ? `$${(value/1000).toFixed(0)}K` : formatNumber(value)} 
                    style={{fontSize: '12px', fill: 'white'}} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Cohort Chart */}
          <div className="chart-card" style={{padding: '15px 0px'}}>
            <h3 style={{margin: '0 0 15px 20px'}}>
              {viewMode === 'visits' ? 'Visits' : 'Cost'} by Cohort
            </h3>
            <ResponsiveContainer width="100%" height={450}>
              <BarChart data={data.byCohort} margin={{ top: 5, right: 20, left: 20, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" style={{fontSize: '12px'}} interval={0} />
                <YAxis tickFormatter={viewMode === 'cost' ? (v) => `$${(v/1000).toFixed(0)}K` : undefined} />
                <Tooltip cursor={{ fill: '#e0f2fe' }} content={CustomTooltip} />
                <Bar dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} fill="#0284c7">
                  <LabelList dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} position="top" 
                    formatter={(value) => viewMode === 'cost' ? `$${(value/1000).toFixed(0)}K` : formatNumber(value)} 
                    style={{fontSize: '12px'}} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Risk Group Chart */}
          <div className="chart-card" style={{padding: '15px 0px'}}>
            <h3 style={{margin: '0 0 15px 20px'}}>
              {viewMode === 'visits' ? 'Visits' : 'Cost'} by Risk Group
            </h3>
            <ResponsiveContainer width="100%" height={450}>
              <BarChart data={data.byRiskGroup} margin={{ top: 5, right: 20, left: 20, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" style={{fontSize: '12px'}} interval={0} />
                <YAxis tickFormatter={viewMode === 'cost' ? (v) => `$${(v/1000).toFixed(0)}K` : undefined} />
                <Tooltip cursor={{ fill: '#e0f2fe' }} content={CustomTooltip} />
                <Bar dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} fill="#0284c7">
                  <LabelList dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} position="top" 
                    formatter={(value) => viewMode === 'cost' ? `$${(value/1000).toFixed(0)}K` : formatNumber(value)} 
                    style={{fontSize: '12px'}} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Table Section */}
        <div className="table-section" style={{marginTop: '30px', marginLeft: '15px', marginRight: '15px', marginBottom: '20px'}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
            <h2 style={{margin: 0}}>PHY Visits ({formatNumber(beneficiaries.length)} records)</h2>
            <button 
              onClick={downloadCSV}
              style={{
                padding: '10px 20px',
                backgroundColor: '#0369a1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#0284c7'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#0369a1'}
            >
              Download CSV
            </button>
          </div>
          <div style={{overflowX: 'auto', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
            <table style={{width: '100%', borderCollapse: 'collapse'}}>
              <thead>
                <tr style={{backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb'}}>
                  {displayColumns.map((column) => (
                    <th key={column} style={{padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap'}}>
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {beneficiaries.map((row, idx) => (
                  <tr key={`${row.CUR_CLM_UNIQ_ID || row.BENE_MBI_ID || idx}`} style={{borderBottom: '1px solid #e5e7eb', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb'}}>
                    {displayColumns.map((column) => (
                      <td key={column} style={{padding: '10px 12px', fontSize: '12px', color: '#374151'}}>
                        {row[column]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>
      </div>
    </div>
  );
}

export default PHYDashboardMain;
